/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import {
  IClinicCallForwardingRules,
  IClinicE911Address,
  IClinicInternalExtension,
  IClinicNumber,
  IClinicPhoneDevice,
  ISipCredentialsOnce,
  ITwilioAvailableNumber,
  ITwilioPurchaseNumberPricing,
  IClinicVoicemailSettings,
  IWorkingHours,
  RegisterE911TwilioPayload,
  E911TwilioRegistrationResult,
  PurchasedTwilioNumber,
} from '@dataTypes/clinic.type'
import { ITwilioPortability } from '@dataTypes/twilio.types'
import { toApiError } from './errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CLINIC')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CLINIC')

const baseHeaders = {
  'Content-Type': 'application/json',
}

const getHeaders = (): Record<string, string> => {
  const dbName = localStorage.getItem('db_name')
  return dbName ? { ...baseHeaders, 'x-db-name': dbName } : baseHeaders
}

const getHeadersMultipart = (): Record<string, string> => {
  const dbName = localStorage.getItem('db_name')
  return dbName ? { 'x-db-name': dbName } : {}
}

type ApiResponse<T> = { statusCode: number; message: string; data: T }

export const getClinicNumbers = async (): Promise<
  ApiResponse<{ clinic_numbers?: IClinicNumber[] }>
> => {
  try {
    const res = await axios.get(`${BUSINESS_LAYER_API}/api/v1/clinic/number`, {
      headers: getHeaders(),
    })
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicNumber = async (
  clinic_number_id: string,
  data: Partial<IClinicNumber>,
): Promise<ApiResponse<{ clinic_number?: IClinicNumber }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number`,
      {
        clinic_number_id,
        data,
      },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export type BulkWorkingHoursItemRequest = {
  clinic_number_id: string
  same_working_hours?: boolean
  apply_working_hours?: boolean
  working_hours: {
    day_of_week: string
    timezone: string
    name?: string
    start_time: string
    end_time: string
  }[]
}

export const bulkUpdateClinicNumbersWorkingHours = async (
  items: BulkWorkingHoursItemRequest[],
): Promise<ApiResponse<{ clinic_numbers?: IClinicNumber[] }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/bulk/working-hours`,
      { items },
      { headers: baseHeaders },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const setMainClinicNumber = async (
  clinic_id: string,
  clinic_number_id: string,
): Promise<ApiResponse<{ clinic_numbers?: IClinicNumber[] }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/active`,
      {
        clinic_id,
        clinic_number_id,
      },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicNumberWorkingHours = async (
  clinic_number_id: string,
  working_hours: Array<
    Pick<IWorkingHours, 'day_of_week' | 'start_time' | 'end_time'>
  >,
): Promise<ApiResponse<{ clinic_number?: IClinicNumber }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/${clinic_number_id}/working-hours`,
      { working_hours },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicNumberForwarding = async (
  clinic_number_id: string,
  rules: Partial<IClinicCallForwardingRules>,
): Promise<ApiResponse<{ clinic_number?: IClinicNumber }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/${clinic_number_id}/forwarding`,
      { rules },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicNumberVoicemail = async (
  clinic_number_id: string,
  settings: Partial<IClinicVoicemailSettings>,
): Promise<ApiResponse<{ clinic_number?: IClinicNumber }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/${clinic_number_id}/voicemail`,
      { settings },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteClinicNumber = async (
  clinic_number_id: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const res = await axios.delete(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number/${clinic_number_id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const releaseTwilioNumber = async (payload: {
  sid: string
  token: string
  phoneNumber: string
  messageServiceSid: string
}): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/release-number`,
      payload,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const applyClinicNumberConfigToTwilio = async (
  clinic_number_id: string,
): Promise<ApiResponse<{ voice_url?: string }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/number/${clinic_number_id}/apply-config`,
      {},
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getClinicE911Addresses = async (
  clinic_id: string,
): Promise<ApiResponse<{ e911_addresses?: IClinicE911Address[] }>> => {
  try {
    const res = await axios.get(
      `${BUSINESS_LAYER_API}/api/v1/clinic/e911-addresses?clinic_id=${clinic_id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createClinicE911Address = async (
  payload: IClinicE911Address,
): Promise<ApiResponse<{ e911_address?: IClinicE911Address }>> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/e911-addresses`,
      payload,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicE911Address = async (
  id: string,
  data: Partial<IClinicE911Address>,
): Promise<ApiResponse<{ e911_address?: IClinicE911Address }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/e911-addresses/${id}`,
      { data },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteClinicE911Address = async (
  id: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const res = await axios.delete(
      `${BUSINESS_LAYER_API}/api/v1/clinic/e911-addresses/${id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Internal extensions
export const getInternalExtensions = async (
  clinic_id: string,
): Promise<
  ApiResponse<{ internal_extensions?: IClinicInternalExtension[] }>
> => {
  try {
    const res = await axios.get(
      `${BUSINESS_LAYER_API}/api/v1/clinic/internal-extensions?clinic_id=${clinic_id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createInternalExtension = async (
  payload: IClinicInternalExtension,
): Promise<ApiResponse<{ internal_extension?: IClinicInternalExtension }>> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/internal-extensions`,
      payload,
      { headers: baseHeaders },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateInternalExtension = async (
  id: string,
  data: Partial<IClinicInternalExtension>,
): Promise<ApiResponse<{ internal_extension?: IClinicInternalExtension }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/internal-extensions/${id}`,
      { data },
      { headers: baseHeaders },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteInternalExtension = async (
  id: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const res = await axios.delete(
      `${BUSINESS_LAYER_API}/api/v1/clinic/internal-extensions/${id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPhoneDevices = async (
  clinic_id: string,
): Promise<ApiResponse<{ phone_devices?: IClinicPhoneDevice[] }>> => {
  try {
    const res = await axios.get(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices?clinic_id=${clinic_id}`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createPhoneDevice = async (
  payload: IClinicPhoneDevice & { tenant_name?: string },
): Promise<
  ApiResponse<{
    phone_device?: IClinicPhoneDevice
    sip_credentials_once?: ISipCredentialsOnce
  }>
> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices`,
      payload,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updatePhoneDevice = async (
  id: string,
  data: Partial<IClinicPhoneDevice>,
): Promise<ApiResponse<{ phone_device?: IClinicPhoneDevice }>> => {
  try {
    const res = await axios.put(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices/${id}`,
      { data },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deletePhoneDevice = async (
  id: string,
  clinic_id?: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const res = await axios.delete(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices/${id}`,
      {
        headers: getHeaders(),
        params: clinic_id ? { clinic_id } : undefined,
      },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const regenerateDeviceSipCredentials = async (
  id: string,
  clinic_id: string,
): Promise<ApiResponse<{ sip_credentials_once?: ISipCredentialsOnce }>> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices/${id}/regenerate-sip`,
      { clinic_id },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const ringPhoneDevice = async (
  id: string,
  clinic_id: string,
  from_number?: string,
): Promise<ApiResponse<{ callSid?: string | null }>> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/phone-devices/${id}/ring`,
      { clinic_id, ...(from_number ? { from_number } : {}) },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createDeviceE911Address = async (payload: {
  clinic_id: string
  clinic_phone_device_id: string
  customer_name: string
  address: string
  city: string
  state: string
  zip: string
  iso_country?: string
}): Promise<
  ApiResponse<{
    e911_address?: IClinicE911Address
    validation_status?: 'pending' | 'verified' | 'failed'
    error_message?: string
  }>
> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/e911-addresses/for-device`,
      payload,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const checkPortabilityExternal = async (
  phone_numbers: string[],
): Promise<ApiResponse<{ portability?: ITwilioPortability[] }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/check-portability`,
      phone_numbers,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPurchaseNumberPricingExternal = async (
  countryCode: string,
  sid: string,
  token: string,
): Promise<ApiResponse<ITwilioPurchaseNumberPricing>> => {
  try {
    const res = await axios.get(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/purchase-number-pricing`,
      {
        params: { countryCode, sid, token },
        headers: getHeaders(),
      },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAvailablePurchaseNumbersExternal = async (params: {
  numberType: 'local' | 'toll-free'
  countryCode: string
  latitude: string
  longitude: string
  sid: string
  token: string
}): Promise<ApiResponse<{ available_numbers?: ITwilioAvailableNumber[] }>> => {
  try {
    const res = await axios.get(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/available-numbers`,
      {
        params,
        headers: getHeaders(),
      },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const purchaseTwilioNumberExternal = async (
  phoneNumber: string,
  friendlyName: string,
  sid: string,
  token: string,
): Promise<ApiResponse<{ purchased_number?: PurchasedTwilioNumber }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/purchase`,
      { phoneNumber, friendlyName, sid, token },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createClinicNumber = async (
  payload: IClinicNumber,
): Promise<ApiResponse<{ clinic_number?: IClinicNumber }>> => {
  try {
    const res = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/clinic/number`,
      payload,
      { headers: baseHeaders },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendPortOtpExternal = async (
  phoneNumber: string,
  channel: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const clinicId = localStorage.getItem('clinic_id') || undefined
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/send-otp`,
      { phoneNumber, channel, ...(clinicId ? { clinic_id: clinicId } : {}) },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const verifyPortOtpExternal = async (
  phoneNumber: string,
  otp: string,
): Promise<ApiResponse<{ isVerified?: boolean }>> => {
  try {
    const clinicId = localStorage.getItem('clinic_id') || undefined
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/verify-otp`,
      { phoneNumber, otp, ...(clinicId ? { clinic_id: clinicId } : {}) },
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createPortUploadUrlExternal = async (body: {
  contentType: string
  filename: string
  folder?: string
  clinic_id?: string
  extra_path?: string
}): Promise<ApiResponse<{ upload_url?: string; blob_name?: string }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/create-upload-url`,
      body,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadPortingDocumentExternal = async (
  formData: FormData,
): Promise<ApiResponse<{ document?: { sid?: string } }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/upload-porting-document`,
      formData,
      { headers: getHeadersMultipart() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createPortRequestExternal = async (body: {
  phone_number: string
  document_sids: string[]
  user_fullname: string
  user_email: string
  company_customer_name: string
  customer_type?: string
  admin_email?: string
}): Promise<ApiResponse<{ twilio_port_request?: Record<string, unknown> }>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/create-port-request`,
      body,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

/**
 * Twilio (external layer): register emergency address and attach to the incoming phone number.
 * Requires clinic Twilio subaccount credentials and `number_sid` on the clinic number.
 */
export const registerClinicE911AddressWithTwilio = async (
  clinic_number_id: string,
  payload: RegisterE911TwilioPayload,
): Promise<ApiResponse<E911TwilioRegistrationResult>> => {
  try {
    const res = await axios.post(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/number/${clinic_number_id}/register-e911-address`,
      payload,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

/** Twilio (external layer): read emergency linkage status for the number from Twilio. */
export const getClinicNumberE911TwilioStatus = async (
  clinic_number_id: string,
): Promise<ApiResponse<E911TwilioRegistrationResult>> => {
  try {
    const res = await axios.get(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/number/${clinic_number_id}/e911-twilio-status`,
      { headers: getHeaders() },
    )
    return res.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}
